import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CCol, CRow, CForm, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSelect, CButton, CAlert, CBadge,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilX } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import { cilDollar } from '@coreui/icons'
import { CCard, CCardHeader, CCardBody } from '@coreui/react'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'

const columnas: WwColumn[] = [
  { key: 'egresosNro', title: 'Nro.', type: 'number' },
  { key: 'egresosFecha', title: 'Fecha', type: 'date' },
  { key: 'egresosHora', title: 'Hora' },
  { key: 'zonaNombre', title: 'Ruta Nombre' },
  { key: 'cobradorNombre', title: 'Cobrador Usuario' },
  // [RGM] 2026-10-15 (pedido Rafael — "mostrar una columna creador para no
  // equivocarnos y saber quién lo hizo"): quién CREÓ el egreso
  // (EgresosCreadoPor) — distinto del cobrador ASIGNADO a la ruta (pueden
  // coincidir en cuentas admin-cobrador; el móvil ya lo mostraba como
  // "realizadoPor").
  { key: 'egresosCreadoPor', title: 'Creador' },
  { key: 'cajaNro', title: 'Caja Nro', type: 'number' },
  { key: 'cajaNombre', title: 'Caja Nombre' },
  { key: 'egresosTipo', title: 'Tipo' },
  { key: 'egresosValor', title: 'Valor', type: 'currency' },
  { key: 'egresosObservacion', title: 'Observacion' },
]

// Egresos — réplica de egresosgeneral.cs:55 (egresosgeneral GX).
const EgresosPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // Catálogo de zonas (el select de Zona muestra el NOMBRE). Cada zona trae su
  // CAJA (cajaNro/cajaNombre — GET /rutas joinea Cajas, RutasController.cs:189-201):
  // la ruta arrastra su caja y el usuario NO la elige (egresos.cs:1688 gxLoad_27).
  const [zonas, setZonas] = useState<{
    zonaCodigo: number; zonaNombre: string; cajaNro?: number; cajaNombre?: string
  }[]>([])
  // Catálogo de cajas — respaldo para resolver el NOMBRE de la caja de la
  // ruta (A110CajaNombre) si la zona elegida no lo trae.
  const [cajas, setCajas] = useState<{ cajaNro: number; cajaNombre: string }[]>([])
  // ── Filtros rápidos: ruta y cobrador (los más usados) ──
  const [filtroRuta, setFiltroRuta] = useState('')
  const [filtroCobrador, setFiltroCobrador] = useState('')
  const [rutas, setRutas] = useState<string[]>([])
  const [cobradores, setCobradores] = useState<string[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Fila en edición (egresosww.cs 'DO UPDATE' → webobject egresos Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [errorModal, setErrorModal] = useState('')
  const [form, setForm] = useState({
    zonaCodigo: '', cajaNro: '', tipo: 'Faltante', valor: '', observacion: '',
  })

  // Dominio de los filtros (GET /rutas y GET /cobradores — mismos
  // endpoints de la barra de Préstamos).
  useEffect(() => {
    api.get<any[]>('/rutas?empresa=' + empresa)
      .then((r) => {
        setRutas([...new Set(r.map((x: any) => String(x.zonaNombre ?? '').trim()).filter(Boolean))])
        setZonas(r.map((x: any) => ({
          zonaCodigo: x.zonaCodigo,
          zonaNombre: x.zonaNombre,
          cajaNro: x.cajaNro,
          cajaNombre: x.cajaNombre,
        })))
      })
      .catch(() => undefined)
    api.get<any[]>(`/cajas/${empresa}/resumen`).then(setCajas).catch(() => undefined)
    api.get<any[]>('/cobradores?empresa=' + empresa)
      .then((cb) => setCobradores([...new Set(cb.map((x: any) => String(x.usuario ?? '').trim()).filter(Boolean))]))
      .catch(() => undefined)
  }, [empresa])

  // Filas filtradas por ruta/cobrador (client-side, como Préstamos)
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroRuta && String(row.zonaNombre ?? '').trim() !== filtroRuta) return false
    if (filtroCobrador && String(row.cobradorNombre ?? '').trim() !== filtroCobrador) return false
    return true
  }), [rows, filtroRuta, filtroCobrador])

  const filtrosActivos = [
    filtroRuta ? { clave: 'ruta', label: `Ruta: ${filtroRuta}` } : null,
    filtroCobrador ? { clave: 'cobrador', label: `Cobrador: ${filtroCobrador}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]

  const limpiarFiltros = () => { setFiltroRuta(''); setFiltroCobrador('') }

  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/egresos?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  const abrirNuevo = () => {
    setEditando(null)
    setForm({
      zonaCodigo: '', cajaNro: '', tipo: 'Faltante', valor: '', observacion: '',
    })
    setAbierto(true)
  }

  // Modificar: carga la fila en el form y abre el modal. Campos editables
  // del webobject (egresos.cs:3111-3134: Valor, Observacion, Tipo y Zona);
  // EgresosNro/Fecha/Hora no son editables (egresos.cs:3135-3140).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      zonaCodigo: String(fila.zonaCodigo ?? ''),
      cajaNro: String(fila.cajaNro ?? ''),
      tipo: fila.egresosTipo ?? 'Faltante',
      valor: String(fila.egresosValor ?? ''),
      observacion: fila.egresosObservacion ?? '',
    })
    setAbierto(true)
  }

  // Al elegir/cambiar la RUTA se carga SU caja en el form (egresos.cs:1688
  // gxLoad_27: la zona trae ZonaNombre + CajaNro; el campo Caja es de SOLO
  // LECTURA — edtCajaNro_Enabled/edtCajaNombre_Enabled = 0, egresos.cs:2531/2622).
  const cambiarZona = (zonaCodigo: string) => {
    const zona = zonas.find((z) => String(z.zonaCodigo) === zonaCodigo)
    setForm({
      ...form,
      zonaCodigo,
      cajaNro: zona?.cajaNro != null ? String(zona.cajaNro) : '',
    })
  }

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setError('')
    setGuardando(true)
    try {
      if (editando) {
        // DO UPDATE: webobject egresos Mode="UPD" con EgresosNro (la llave
        // viaja en la URL). El backend replica BC000E16 (egresos_bc.cs:2261)
        // y recalcula caja/cobrador (egresos_bc.cs:893-967).
        await api.put(`/egresos/${editando.egresosNro}`, {
          empresaCodigo: empresa,
          zonaCodigo: Number(form.zonaCodigo),
          tipo: form.tipo,
          valor: Number(form.valor),
          observacion: form.observacion || undefined,
        })
      } else {
        await api.post('/egresos', {
          empresaCodigo: empresa,
          zonaCodigo: Number(form.zonaCodigo),
          tipo: form.tipo,
          valor: Number(form.valor),
          observacion: form.observacion || undefined,
        })
      }
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminar = (fila: any) => {
    // DO DELETE: webobject egresos Mode="DLT" con EgresosNro. El backend
    // replica BC000E17 (egresos_bc.cs:2262) y revierte caja/cobrador
    // (egresos_bc.cs:893-967) — sin control de referencias (hoja).
    if (!window.confirm(`¿Eliminar el egreso ${fila.egresosNro}?`)) return
    api.delete(`/egresos/${fila.egresosNro}?empresa=${empresa}`)
      .then(() => {
        setAviso('Egreso eliminado.')
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)

  // Nombre de la caja de la RUTA elegida (la ruta arrastra su caja). Fuente:
  // cajaNombre de la zona o, de respaldo, el catálogo de cajas ya cargado.
  // Sin ruta elegida o caja no hallada → vacío (campo deshabilitado).
  const cajaNombreSeleccionada = (() => {
    const zona = zonas.find((z) => String(z.zonaCodigo) === form.zonaCodigo)
    const deZona = String(zona?.cajaNombre ?? '').trim()
    if (deZona) return deZona
    return String(cajas.find((cj) => String(cj.cajaNro) === form.cajaNro)?.cajaNombre ?? '').trim()
  })()

  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Egresos</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <WwGrid
        filters={
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end">
            <CFormSelect className="w-auto" value={filtroRuta} onChange={(e) => setFiltroRuta(e.target.value)}>
              <option value="">Todas las Rutas</option>
              {rutas.map((r) => <option key={r} value={r}>{r}</option>)}
            </CFormSelect>
            <CFormSelect className="w-auto" value={filtroCobrador} onChange={(e) => setFiltroCobrador(e.target.value)}>
              <option value="">Todos los Cobradores</option>
              {cobradores.map((cb) => <option key={cb} value={cb}>{cb}</option>)}
            </CFormSelect>
            {filtrosActivos.length > 0 && (
              <div className="d-flex flex-wrap align-items-center gap-1">
                {filtrosActivos.map((f) => (
                  <CBadge key={f.clave} color="secondary" className="d-inline-flex align-items-center gap-1 fw-normal">
                    {f.label}
                    <CIcon icon={cilX} size="sm" role="button" style={{ cursor: 'pointer' }}
                      onClick={() => { if (f.clave === 'ruta') setFiltroRuta(''); if (f.clave === 'cobrador') setFiltroCobrador('') }} />
                  </CBadge>
                ))}
                <CButton size="sm" color="outline-secondary" onClick={limpiarFiltros}>Limpiar filtros</CButton>
              </div>
            )}
          </div>
        }
        title="Egresos"
        columns={columnas}
        rows={visibleRows}
        onTotalChange={setTotalReg}
        loading={cargando}
        onAgregar={abrirNuevo}
        onEditar={abrirEditar}
        onEliminar={eliminar}
        busquedaPlaceholder="Buscar por egreso, ruta o cobrador..." 
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar egreso' : 'Registrar egreso'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            {/* Tarjeta única (patrón formularios nuevos) — decisión Rafael */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilDollar} size="sm" /> Datos del Egreso
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  {editando && (
                    <CCol md={6}><CFormLabel>Nro</CFormLabel><CFormInput value={editando.egresosNro} disabled /></CCol>
                  )}
                  {editando && (
                    <CCol md={6}><CFormLabel>Fecha</CFormLabel><CFormInput type="date" value={String(editando.egresosFecha ?? '').slice(0, 10)} disabled /></CCol>
                  )}
                  <CCol md={6}>
                    {/* Select de ZONA por NOMBRE (el backend recibe zonaCodigo) */}
                    <CFormLabel>Zona</CFormLabel>
                    <CFormSelect value={form.zonaCodigo} onChange={(e) => cambiarZona(e.target.value)}>
                      <option value="">Seleccione...</option>
                      {zonas.map((z) => <option key={z.zonaCodigo} value={z.zonaCodigo}>{z.zonaNombre}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    {/* Caja — SOLO LECTURA SIEMPRE: es la caja de la RUTA
                        elegida, no se elige a mano (egresos.cs:1688 gxLoad_27
                        carga CajaNro con la zona; edtCajaNro/edtCajaNombre
                        deshabilitados — egresos.cs:2531/2622). El nombre sale
                        de la zona (cajaNombre) o del catálogo /cajas/resumen. */}
                    <CFormLabel>Caja</CFormLabel>
                    <CFormInput
                      value={cajaNombreSeleccionada}
                      disabled
                      placeholder={form.zonaCodigo ? '' : 'Seleccione una zona...'}
                    />
                  </CCol>
                  <CCol md={6}>
                    {/* Tipos de egreso del GX (egresosww.cs:1377-1380): Operativos,
                        Nomina, Faltante, Vale, X_abono, Otros */}
                    <CFormLabel>Tipo</CFormLabel>
                    <CFormSelect value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                      <option value="">Seleccione...</option>
                      <option value="Operativos">Operativos</option>
                      <option value="Nomina">Nomina</option>
                      <option value="Faltante">Faltante</option>
                      <option value="Vale">Vale</option>
                      <option value="X_abono">X_abono</option>
                      <option value="Otros">Otros</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel>Valor</CFormLabel>
                    {/* [RGM] 2026-10-02 (moneda global, pedido Rafael): prefijo
                        de la moneda configurada + formato de miles en vivo. */}
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.valor}
                        onValueChange={(v) => setForm({ ...form, valor: v.value })}
                        thousandSeparator=","
                        decimalSeparator="."
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={12}><CFormLabel>Observación</CFormLabel><CFormInput value={form.observacion} onChange={(e) => setForm({ ...form, observacion: capitalizarPalabras(e.target.value) })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Registrar egreso'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default EgresosPage